import axios from 'axios';
import CryptoJS from 'crypto-js';
import {
  loanBackendUrl,
  mobileOtpRequestHeaders,
  saveEnquiryUrl,
  saveLendingPageUrl,
  taskApiDirectUrl,
  taskApiPostUrl,
  taskApiUrl,
} from './taskApi';

/** Obfuscated action codes (real paths only on server gateway) */
export const GW = {
  GET_STATE: 'a1',
  GET_CITY: 'a2',
  GET_PINCODE: 'a3',
  OTP_SEND: 'm1',
  OTP_VERIFY: 'm2',
  VERIFY_PAN: 'p1',
  PAN_BLACKLIST: 'p2',
  PAN_CUSTOMER: 'p3',
  AADHAAR_SEND: 'k1',
  AADHAAR_VERIFY: 'k2',
  SAVE_ENQUIRY: 'e1',
  SAVE_LEAD: 'l1',
};

const AES_KEY = CryptoJS.enc.Utf8.parse('LxQY43Nc&87x8nxw');
const AES_IV = CryptoJS.enc.Utf8.parse('@@@@&&&&####$$$$');

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return process.env.NODE_ENV === 'development';
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

/** Live (mrrupya.com) → gateway; localhost → direct APIs (safe for local) */
export const shouldUseApiGateway = () => {
  if (process.env.REACT_APP_FORCE_API_GATEWAY === '1') return true;
  if (process.env.REACT_APP_DISABLE_API_GATEWAY === '1') return false;
  return !isLocalDevHost();
};

export const apiGatewayUrl = () => '/api/gateway.php';

export const encryptGatewayPayload = (payload) => {
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload ?? {}), AES_KEY, {
    iv: AES_IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  // Raw ciphertext Base64 — matches PHP openssl_decrypt(..., 0, iv)
  return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
};

export const buildGatewayEnvelope = (action, payload = {}) => ({
  a: action,
  d: encryptGatewayPayload(payload),
});

/**
 * Local/direct fallback — same behaviour as before gateway.
 * Returns axios response shape { data, status, ... }
 */
const legacyRequest = async (action, payload = {}, options = {}) => {
  const timeout = options.timeout || 30000;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  switch (action) {
    case GW.GET_STATE:
      return axios.get(taskApiPostUrl('getState'), { timeout });
    case GW.GET_CITY:
      return axios.post(taskApiPostUrl('getCity'), payload, { headers: jsonHeaders, timeout });
    case GW.GET_PINCODE:
      return axios.post(taskApiPostUrl('getPincodeByCityId'), payload, {
        headers: jsonHeaders,
        timeout,
      });
    case GW.OTP_SEND: {
      const params = new URLSearchParams();
      params.append('mobile', String(payload.mobile || '').trim());
      return axios.post(taskApiUrl('sendOtp'), params, {
        headers: mobileOtpRequestHeaders(),
        timeout,
      });
    }
    case GW.OTP_VERIFY: {
      const params = new URLSearchParams();
      params.append('mobile', String(payload.mobile || '').trim());
      params.append('otp', String(payload.otp || '').trim());
      return axios.post(taskApiUrl('verifyOtp'), params, {
        headers: mobileOtpRequestHeaders(),
        timeout,
      });
    }
    case GW.VERIFY_PAN:
      return axios.post(loanBackendUrl('verifyPan.php'), payload, {
        headers: jsonHeaders,
        timeout,
      });
    case GW.PAN_BLACKLIST: {
      const pan = encodeURIComponent(String(payload.pancard || payload.pan || '').trim());
      return axios.get(`${taskApiDirectUrl('checkGetCustomerbyPanBlacklist')}?pancard=${pan}`, {
        timeout,
      });
    }
    case GW.PAN_CUSTOMER: {
      const pan = encodeURIComponent(String(payload.pancard || payload.pan || '').trim());
      return axios.get(`${taskApiDirectUrl('checkGetCustomerbyPan')}?pancard=${pan}`, {
        timeout,
      });
    }
    case GW.AADHAAR_SEND:
      return axios.post(loanBackendUrl('sendAadhaarOtp.php'), payload, {
        headers: jsonHeaders,
        timeout,
      });
    case GW.AADHAAR_VERIFY:
      return axios.post(loanBackendUrl('verifyAadhaarOtp.php'), payload, {
        headers: jsonHeaders,
        timeout,
      });
    case GW.SAVE_ENQUIRY: {
      const url = saveEnquiryUrl();
      const resp = await fetch(url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(payload),
        mode: 'cors',
        keepalive: Boolean(options.keepalive),
      });
      let data = null;
      try {
        data = await resp.json();
      } catch {
        data = null;
      }
      return { data, status: resp.status, ok: resp.ok };
    }
    case GW.SAVE_LEAD:
      return axios.post(saveLendingPageUrl(), payload, { headers: jsonHeaders, timeout });
    default:
      throw new Error('Unknown gateway action');
  }
};

/**
 * Unified API call. Live → encrypted /api/gateway.php; local → direct.
 * @returns {Promise<{ data: any, status?: number }>}
 */
export const apiGateway = async (action, payload = {}, options = {}) => {
  if (!shouldUseApiGateway()) {
    return legacyRequest(action, payload, options);
  }

  const envelope = buildGatewayEnvelope(action, payload);
  const timeout = options.timeout || 45000;

  if (options.keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
      const ok = navigator.sendBeacon(apiGatewayUrl(), blob);
      if (ok) return { data: { status: true, beacon: true }, status: 200 };
    } catch {
      // fall through to fetch
    }
  }

  if (options.keepalive) {
    const resp = await fetch(apiGatewayUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(envelope),
      keepalive: true,
    });
    let data = null;
    try {
      data = await resp.json();
    } catch {
      data = null;
    }
    return { data, status: resp.status };
  }

  return axios.post(apiGatewayUrl(), envelope, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    timeout,
  });
};
